const SVG_NS = "http://www.w3.org/2000/svg";

const headingTexture = () => {
	if (isSafariBrowser()) {
		document.documentElement.classList.add("is-safari");
	}

	const targets = document.querySelectorAll(".textured");
	if (!targets.length) return;

	targets.forEach((texturedEl) => {
		if (texturedEl.dataset.textureInitialized === "true") return;
		texturedEl.dataset.textureInitialized = "true";

		const bgImage = getBackgroundImage(texturedEl);
		const textureUrl = getUrlFromBackground(bgImage);

		if (!textureUrl) return;

		new HeadingTextureLiquid(texturedEl, textureUrl);
	});
};

const getBackgroundImage = (element) => {
	const parentEl = element.closest(".texture-header") || element.parentElement;
	const sources = [
		element.style.backgroundImage,
		window.getComputedStyle(element).backgroundImage,
		parentEl?.style.backgroundImage,
		parentEl ? window.getComputedStyle(parentEl).backgroundImage : "",
	];

	return sources.find((source) => source && source !== "none") || "";
};

const getUrlFromBackground = (backgroundImage) => {
	const match = backgroundImage.match(/url\((['"]?)(.*?)\1\)/);
	return match ? match[2] : "";
};

const getSvgTextAlign = (textAlign) => {
	if (textAlign === "right" || textAlign === "end") {
		return {
			anchor: "end",
		};
	}

	if (textAlign === "center") {
		return {
			anchor: "middle",
		};
	}

	return {
		anchor: "start",
	};
};

const isSafariBrowser = () => {
	const ua = window.navigator.userAgent;
	const isAppleWebKit = /Safari/i.test(ua) && /AppleWebKit/i.test(ua);
	const isOtherIosBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
	const isChromeLike = /Chrome|Chromium|Android/i.test(ua);

	return isAppleWebKit && !isOtherIosBrowser && !isChromeLike;
};

class HeadingTextureLiquid {
	constructor(element, textureUrl) {
		this.el = element;
		this.textureUrl = textureUrl;
		this.isSafari = isSafariBrowser();
		this.svg = null;
		this.wrapper = null;
		this.mask = null;
		this.maskText = null;
		this.baseRect = null;
		this.textAlign = "center";
		this.originalLines = getMarkupLines(element);
		this.hasExplicitLineBreaks = this.originalLines.length > 1;
		this.originalText = this.originalLines.join(" ");
		this.lines = [];
		this.particles = [];

		this.mouse = {
			x: 0,
			y: 0,
			offsetX: 0,
			offsetY: 0,
			smoothX: 0,
			smoothY: 0,
			diff: 0,
			hasMoved: false,
		};

		this.head = {
			x: 0,
			y: 0,
			sp: 0,
			p: 0,
		};

		this.isPaused = false;
		this.width = 0;
		this.height = 0;
		this.frame = 0;
		this.renderedFrame = 0;
		this.emitEvery = this.isSafari ? 2 : 1;
		this.maxParticles = this.isSafari ? 24 : 80;
		this.tick = this.tick.bind(this);

		this.init();
	}

	init() {
		this.createSvg();
		this.setFilter();
		this.bindEvents();
		this.observeViewport();
		requestAnimationFrame(this.tick);
	}

	createSvg() {
		const maskId = `heading-texture-mask-${Math.random().toString(36).slice(2)}`;
		const filterId = `heading-texture-gooey-${Math.random().toString(36).slice(2)}`;
		const computed = window.getComputedStyle(this.el);
		const blur = this.isSafari ? 10 : 20;
		const alphaMatrix = this.isSafari ? "35 -8" : "50 -10";

		this.el.style.backgroundImage = "none";
		this.el.style.color = "transparent";
		this.el.style.webkitTextFillColor = "transparent";
		this.el.style.position =
			computed.position === "static" ? "relative" : computed.position;

		this.svg = document.createElementNS(SVG_NS, "svg");
		this.svg.classList.add("heading-texture-liquid");
		this.svg.setAttribute("xmlns", SVG_NS);
		this.svg.setAttribute("preserveAspectRatio", "none");
		this.svg.setAttribute("aria-hidden", "true");
		this.svg.style.position = "absolute";
		this.svg.style.top = "0";
		this.svg.style.left = "0";
		this.svg.style.width = "100%";
		this.svg.style.height = "100%";
		this.svg.style.overflow = "visible";
		this.svg.style.pointerEvents = "none";
		this.svg.style.zIndex = "2";

		this.svg.innerHTML = `
			<defs>
				<mask
					id="${maskId}"
					maskUnits="userSpaceOnUse"
					maskContentUnits="userSpaceOnUse"
					mask-type="alpha"
				>
					<text
						x="0"
						y="0"
						fill="white"
						dominant-baseline="middle"
						text-anchor="start"
					></text>
				</mask>

				<filter
					id="${filterId}"
					x="-50%"
					y="-50%"
					width="200%"
					height="200%"
				>
					<feImage
						result="texture"
						href="${escapeAttribute(this.textureUrl)}"
						x="0"
						y="0"
						width="100%"
						height="100%"
						preserveAspectRatio="none"
					></feImage>

					<feGaussianBlur
						in="SourceGraphic"
						stdDeviation="${blur}"
					></feGaussianBlur>

					<feColorMatrix
						type="matrix"
						values="
							1 0 0 0 0
							0 1 0 0 0
							0 0 1 0 0
							0 0 0 ${alphaMatrix}"
						result="goo"
					></feColorMatrix>

					<feComposite
						in="texture"
						in2="goo"
						operator="in"
					></feComposite>
				</filter>
			</defs>

			<rect
				x="0"
				y="0"
				width="100%"
				height="100%"
				class="heading-texture-liquid__base"
				fill="#E1E1E1"
				mask="url(#${maskId})"
			></rect>

			<g
				class="heading-texture-liquid__wrapper"
				filter="url(#${filterId})"
				mask="url(#${maskId})"
			></g>
		`;

		this.mask = this.svg.querySelector("mask");
		this.maskText = this.svg.querySelector("mask text");
		this.baseRect = this.svg.querySelector(".heading-texture-liquid__base");
		this.wrapper = this.svg.querySelector(".heading-texture-liquid__wrapper");
		this.el.appendChild(this.svg);
		this.syncTextStyles();
	}

	syncTextStyles() {
		const computed = window.getComputedStyle(this.el);

		this.lines = this.hasExplicitLineBreaks
			? this.originalLines
			: getFittingLines(this.el, computed, this.originalText);
		this.textAlign = getSvgTextAlign(computed.textAlign);
		this.maskText.style.fontFamily = computed.fontFamily;
		this.maskText.style.fontSize = computed.fontSize;
		this.maskText.style.fontWeight = computed.fontWeight;
		this.maskText.style.fontStyle = computed.fontStyle;
		this.maskText.style.lineHeight = computed.lineHeight;
		this.maskText.style.textTransform = computed.textTransform;
		this.maskText.style.dominantBaseline = "middle";
		this.renderMaskText(computed);
	}

	getTextX() {
		if (this.textAlign.anchor === "end") return this.width;
		if (this.textAlign.anchor === "middle") return this.width * 0.5;

		return 0;
	}

	renderMaskText(computed) {
		const fontSize = parseFloat(computed.fontSize) || 16;
		const lineHeight = parseFloat(computed.lineHeight) || fontSize;
		const lines = this.lines.length ? this.lines : [this.originalText];
		const initialDy = -((lines.length - 1) * lineHeight) / 2;
		const x = this.getTextX();

		this.maskText.textContent = "";
		this.maskText.setAttribute("x", x);
		this.maskText.setAttribute("text-anchor", this.textAlign.anchor);

		lines.forEach((line, index) => {
			const tspan = document.createElementNS(SVG_NS, "tspan");

			tspan.setAttribute("x", x);
			tspan.setAttribute("dy", index === 0 ? `${initialDy}px` : `${lineHeight}px`);
			tspan.textContent = line;
			this.maskText.appendChild(tspan);
		});
	}

	bindEvents() {
		window.addEventListener("resize", () => {
			this.setFilter();
			this.syncTextStyles();
		});

		this.el.addEventListener("mousemove", (event) => {
			this.onPointerMove(event);
		});

		this.el.addEventListener(
			"touchmove",
			(event) => {
				this.onPointerMove(event);
			},
			{ passive: true },
		);
	}

	observeViewport() {
		if (!("IntersectionObserver" in window)) return;

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				this.isPaused = !entry.isIntersecting;
			});
		});

		observer.observe(this.el);
	}

	onPointerMove(event) {
		let clientX = 0;
		let clientY = 0;
		const rect = this.el.getBoundingClientRect();

		if (event.type === "touchmove") {
			clientX = event.touches[0].clientX;
			clientY = event.touches[0].clientY;
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}

		this.mouse.offsetX = rect.left;
		this.mouse.offsetY = rect.top;
		this.width = rect.width;
		this.height = rect.height;
		this.setSvgBounds();
		this.mouse.x = clientX - rect.left;
		this.mouse.y = clientY - rect.top;
		this.mouse.hasMoved = true;
	}

	setFilter() {
		const rect = this.el.getBoundingClientRect();

		this.mouse.offsetX = rect.left;
		this.mouse.offsetY = rect.top;
		this.width = rect.width;
		this.height = rect.height;

		this.setSvgBounds();

		if (!this.mouse.hasMoved) {
			this.mouse.x = this.width * 0.5;
			this.mouse.y = this.height * 0.5;
			this.mouse.smoothX = this.mouse.x;
			this.mouse.smoothY = this.mouse.y;
		}
	}

	setSvgBounds() {
		const computed = window.getComputedStyle(this.el);
		const fontSize = parseFloat(computed.fontSize) || 16;
		const paddingX = fontSize * 0.12;
		const paddingY = fontSize * 0.35;
		const viewBoxWidth = this.width + paddingX * 2;
		const viewBoxHeight = this.height + paddingY * 2;

		this.svg.style.top = `${-paddingY}px`;
		this.svg.style.left = `${-paddingX}px`;
		this.svg.style.width = `${viewBoxWidth}px`;
		this.svg.style.height = `${viewBoxHeight}px`;
		this.svg.setAttribute(
			"viewBox",
			`${-paddingX} ${-paddingY} ${viewBoxWidth} ${viewBoxHeight}`,
		);
		this.mask.setAttribute("x", `${-paddingX}`);
		this.mask.setAttribute("y", `${-paddingY}`);
		this.mask.setAttribute("width", `${viewBoxWidth}`);
		this.mask.setAttribute("height", `${viewBoxHeight}`);
		this.maskText.setAttribute("y", `${this.height * 0.56}`);
		this.maskText.setAttribute("x", `${this.getTextX()}`);
		this.maskText.querySelectorAll("tspan").forEach((tspan) => {
			tspan.setAttribute("x", `${this.getTextX()}`);
		});
		this.baseRect.setAttribute("x", `${-paddingX}`);
		this.baseRect.setAttribute("y", `${-paddingY}`);
		this.baseRect.setAttribute("width", `${viewBoxWidth}`);
		this.baseRect.setAttribute("height", `${viewBoxHeight}`);
	}

	emitParticle(time) {
		if (this.particles.length >= this.maxParticles) return;

		let x = 0;
		let y = 0;
		let force = 0;

		if (this.mouse.diff > 0.01) {
			x = this.mouse.smoothX;
			y = this.mouse.smoothY;
			force = this.mouse.diff;
		} else {
			x = this.head.x;
			y = this.head.y;
			force =
				Math.max(10, Math.min(0.075 * window.innerWidth, 150)) *
				(1.25 + 0.25 * Math.pow(Math.cos(0.0003 * time), 2)) *
				this.head.sp;
		}

		const particle = new LiquidParticle(x, y, force, this);

		this.particles.push(particle);
		this.wrapper.appendChild(particle.el);
	}

	tick(time = 0) {
		this.frame += 1;

		if (this.isSafari && this.frame % 2 !== 0) {
			requestAnimationFrame(this.tick);
			return;
		}

		if (!this.isPaused && this.width && this.height) {
			this.renderedFrame += 1;
			this.mouse.smoothX += 0.1 * (this.mouse.x - this.mouse.smoothX);
			this.mouse.smoothY += 0.1 * (this.mouse.y - this.mouse.smoothY);
			this.mouse.diff = Math.hypot(
				this.mouse.x - this.mouse.smoothX,
				this.mouse.y - this.mouse.smoothY,
			);

			this.head.x = 0.5 * this.width + 0.55 * this.width * Math.cos(0.0004 * time);

			this.head.y =
				0.5 * this.height + 0.15 * this.height * Math.sin(0.001 * time);

			this.head.p = this.mouse.diff > 0.01 ? 0 : 1;
			this.head.sp += 0.01 * (this.head.p - this.head.sp);

			if (this.renderedFrame % this.emitEvery === 0 || this.mouse.diff > 0.01) {
				this.emitParticle(time);
			}

			this.updateParticles();
		}

		requestAnimationFrame(this.tick);
	}

	updateParticles() {
		this.particles.forEach((particle) => {
			particle.update();
		});
	}

	removeParticle(particleToRemove) {
		this.particles = this.particles.filter((particle) => {
			return particle !== particleToRemove;
		});
	}
}

class LiquidParticle {
	constructor(x = 0, y = 0, force = 0, parent) {
		this.parent = parent;
		this.r = 0;
		this.life = 0;
		this.duration = 1.25;
		this.size = 5 * Math.sqrt(force) * (0.7 + 0.5 * Math.random());
		this.x = x;
		this.y = y;
		this.seed = 1000 * Math.random();
		this.freq = 0.01 * (0.5 + Math.random());
		this.amplitude = 1 * (1 - 2 * Math.random());
		this.el = document.createElementNS(SVG_NS, "circle");

		this.el.setAttribute("cx", this.x);
		this.el.setAttribute("cy", this.y);
		this.el.setAttribute("r", this.r);
	}

	easeInOutPower1(t) {
		return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
	}

	easeInPower3(t) {
		return t * t * t;
	}

	update() {
		this.life += 1 / 60;

		const frame = performance.now() / 16.6667;

		this.x += Math.cos((frame + this.seed) * this.freq) * this.amplitude;
		this.y += Math.sin((frame + this.seed) * this.freq) * this.amplitude;

		if (this.life <= 0.25) {
			const progress = this.life / 0.25;
			this.r = this.size * this.easeInOutPower1(progress);
		} else {
			const progress = Math.min((this.life - 0.25) / 1, 1);
			this.r = this.size * (1 - this.easeInPower3(progress));
		}

		this.el.setAttribute("cy", this.y);
		this.el.setAttribute("cx", this.x);
		this.el.setAttribute("r", Math.max(0, this.r));

		if (this.life >= this.duration) {
			this.kill();
		}
	}

	kill() {
		this.parent.removeParticle(this);
		this.el.remove();
	}
}

const getMarkupLines = (element) => {
	const lines = [""];

	const appendText = (text) => {
		const cleanText = text.replace(/\s+/g, " ").trim();

		if (!cleanText) return;

		const index = lines.length - 1;
		lines[index] = lines[index] ? `${lines[index]} ${cleanText}` : cleanText;
	};

	const walk = (node) => {
		node.childNodes.forEach((child) => {
			if (child.nodeType === Node.TEXT_NODE) {
				appendText(child.textContent);
				return;
			}

			if (child.nodeType !== Node.ELEMENT_NODE) return;

			const tagName = child.tagName.toLowerCase();

			if (
				tagName === "svg" ||
				child.classList.contains("heading-texture-liquid")
			) {
				return;
			}

			if (tagName === "br") {
				lines.push("");
				return;
			}

			walk(child);
		});
	};

	walk(element);

	const normalizedLines = lines.map((line) => line.trim()).filter(Boolean);

	return normalizedLines.length ? normalizedLines : [element.textContent.trim()];
};

const getFittingLines = (element, computed, text) => {
	const cleanText = text.replace(/\s+/g, " ").trim();

	if (!cleanText) return [];

	const canvas = getFittingLines.canvas || document.createElement("canvas");
	const context = canvas.getContext("2d");
	const fontSize = computed.fontSize || "16px";
	const fontFamily = computed.fontFamily || "sans-serif";
	const fontStyle = computed.fontStyle || "normal";
	const fontWeight = computed.fontWeight || "400";
	const maxWidth = Math.max(1, element.getBoundingClientRect().width);
	const words = cleanText.split(" ");
	const lines = [];

	getFittingLines.canvas = canvas;
	context.font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;

	words.forEach((word) => {
		if (!lines.length) {
			lines.push(word);
			return;
		}

		const currentLine = lines[lines.length - 1] || "";
		const testLine = currentLine ? `${currentLine} ${word}` : word;

		if (context.measureText(testLine).width <= maxWidth) {
			lines[lines.length - 1] = testLine;
			return;
		}

		lines.push(word);
	});

	return lines;
};

const escapeAttribute = (value) => {
	return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
};

export { headingTexture };
